import { useEffect, useMemo, useRef, useState } from "react";
import Plot from "react-plotly.js";
import useProjectStore from "../../store/projectStore";
import { geometryApi } from "../../api/blades";
import { useChartColors } from "../../chartTheme";

function inflectionMarker(x, y, z) {
  return {
    type: "scatter3d", x, y, z,
    mode: "markers",
    marker: { size: 2.5, color: "#111827", opacity: 0.85 },
    hoverinfo: "skip",
    showlegend: false,
  };
}

function computeInflection(data, hasSurface) {
  const nv = data.kappa_v.length;
  const nu = data.kappa_v[0]?.length ?? 0;
  const curvX = [], curvY = [], curvZ = [];
  const skelX = [], skelY = [], skelZ = [];
  for (let iv = 0; iv < nv; iv++) {
    for (let iu = 0; iu < nu - 1; iu++) {
      const k0 = data.kappa_v[iv][iu], k1 = data.kappa_v[iv][iu + 1];
      if (k0 * k1 < 0) {
        const t = k0 / (k0 - k1);
        curvX.push(data.u[iu] + t * (data.u[iu + 1] - data.u[iu]));
        curvY.push(data.v[iv]);
        curvZ.push(0);
        if (hasSurface) {
          skelX.push(data.surface_x[iv][iu] + t * (data.surface_x[iv][iu + 1] - data.surface_x[iv][iu]));
          skelY.push(data.surface_y[iv][iu] + t * (data.surface_y[iv][iu + 1] - data.surface_y[iv][iu]));
          skelZ.push(data.surface_z[iv][iu] + t * (data.surface_z[iv][iu + 1] - data.surface_z[iv][iu]));
        }
      }
    }
  }
  for (let iv = 0; iv < nv - 1; iv++) {
    for (let iu = 0; iu < nu; iu++) {
      const k0 = data.kappa_v[iv][iu], k1 = data.kappa_v[iv + 1][iu];
      if (k0 * k1 < 0) {
        const t = k0 / (k0 - k1);
        curvX.push(data.u[iu]);
        curvY.push(data.v[iv] + t * (data.v[iv + 1] - data.v[iv]));
        curvZ.push(0);
        if (hasSurface) {
          skelX.push(data.surface_x[iv][iu] + t * (data.surface_x[iv + 1][iu] - data.surface_x[iv][iu]));
          skelY.push(data.surface_y[iv][iu] + t * (data.surface_y[iv + 1][iu] - data.surface_y[iv][iu]));
          skelZ.push(data.surface_z[iv][iu] + t * (data.surface_z[iv + 1][iu] - data.surface_z[iv][iu]));
        }
      }
    }
  }
  return { curvX, curvY, curvZ, skelX, skelY, skelZ };
}

export default function CurvaturePage() {
  const { COLOR_PRIMARY, COLOR_LIGHT, COLOR_DARK } = useChartColors();
  const { activeDesign } = useProjectStore();
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  const curvCamRef = useRef({ eye: { x: 0, y: -2.0, z: 1.2 } });
  const skelCamRef = useRef({ eye: { x: 1.5, y: 1.5, z: 0.8 } });

  useEffect(() => {
    if (!activeDesign?.id) return;
    setLoading(true);
    setError(null);
    setData(null);
    geometryApi.curvatureMap(activeDesign.id)
      .then(({ data }) => setData(data))
      .catch(e => setError(e?.response?.data?.error || "Computation failed"))
      .finally(() => setLoading(false));
  }, [activeDesign?.id, activeDesign?.updated_at]);

  const hasSurface = data != null && Array.isArray(data.surface_x) && data.surface_x.length > 0;

  const { curvTraces, skelTraces, zRange, hasInflection } = useMemo(() => {
    if (!data) return { curvTraces: [], skelTraces: [], zRange: [-1e-4, 1e-4], hasInflection: false };

    const flat   = data.kappa_v.flat(1);
    const maxAbs = flat.reduce((m, v) => Math.max(m, Math.abs(v)), 0);
    const zRange = maxAbs < 1e-12 ? [-1e-4, 1e-4] : [-maxAbs, maxAbs];

    const { curvX, curvY, curvZ, skelX, skelY, skelZ } = computeInflection(data, hasSurface);
    const hasInflection = curvX.length > 0;

    const curvTraces = [
      {
        type: "surface", x: data.u, y: data.v, z: data.kappa_v,
        surfacecolor: data.kappa_v,
        colorscale: [[0, COLOR_DARK], [0.5, "#ffffff"], [1, COLOR_PRIMARY]],
        reversescale: false,
        cmin: zRange[0], cmax: zRange[1],
        colorbar: { title: { text: "κᵥ [mm⁻¹]", side: "right", font: { size: 11 } },
                    thickness: 14, len: 0.6, tickfont: { size: 10 } },
        hovertemplate: "u: %{x:.4f}<br>v: %{y:.4f}<br>κᵥ: %{z:.5g} mm⁻¹<extra></extra>",
        opacity: 0.92, lighting: { ambient: 0.8, diffuse: 0.6, specular: 0.1 },
        showlegend: false,
      },
      hasInflection && inflectionMarker(curvX, curvY, curvZ),
    ].filter(Boolean);

    const skelTraces = hasSurface ? [
      {
        type: "surface",
        x: data.surface_x, y: data.surface_y, z: data.surface_z,
        surfacecolor: data.surface_z,
        colorscale: [[0, COLOR_LIGHT], [1, COLOR_DARK]],
        showscale: false, opacity: 0.88,
        lighting: { ambient: 0.75, diffuse: 0.8, specular: 0.25 },
        hovertemplate: "x: %{x:.2f}  y: %{y:.2f}  z: %{z:.2f}<extra></extra>",
        showlegend: false,
      },
      hasInflection && inflectionMarker(skelX, skelY, skelZ),
    ].filter(Boolean) : [];

    return { curvTraces, skelTraces, zRange, hasInflection };
  }, [data, hasSurface]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!activeDesign) return <div className="p-6 text-sm text-gray-500">No design loaded.</div>;
  if (loading)       return <div className="p-6 text-sm text-gray-400">Computing curvature…</div>;
  if (error)         return <div className="p-6 text-sm text-red-500">{error}</div>;
  if (!data)         return null;

  const commonConfig = {
    displayModeBar: "hover",
    modeBarButtons: [["zoom3d", "pan3d", "orbitRotation", "resetCameraDefault3d", "toImage"]],
    scrollZoom: true, responsive: true, displaylogo: false,
  };

  return (
    <div className="flex flex-col" style={{ height: "calc(100vh - 100px)" }}>

      {/* Header */}
      <div className="flex items-center gap-3 px-5 py-2.5 bg-white border-b border-gray-200 shrink-0">
        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
          Spanwise curvature κᵥ
        </span>
        {hasInflection && (
          <>
            <span className="text-xs text-gray-400">·</span>
            <span className="flex items-center gap-1.5 text-xs text-gray-500">
              <span className="inline-flex gap-0.5">
                {[0, 1, 2].map(i => <span key={i} className="inline-block w-1 h-1 rounded-full bg-gray-800" />)}
              </span>
              κᵥ = 0 inflection
            </span>
          </>
        )}
      </div>

      {/* Two-panel body */}
      <div className="flex-1 flex min-h-0">

        {/* Left — κᵥ(u,v) surface */}
        <div className="flex-1 min-w-0 p-3">
          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1 ml-1">
            κᵥ [mm⁻¹] — parameter space
          </p>
          <div style={{ height: "calc(100% - 20px)" }}>
            <Plot
              data={curvTraces}
              layout={{
                margin: { l: 0, r: 0, t: 0, b: 0 },
                paper_bgcolor: "transparent",
                scene: {
                  xaxis: { title: { text: "u (streamwise)", font: { size: 10 } }, gridcolor: "#e5e7eb" },
                  yaxis: { title: { text: "v (spanwise)",   font: { size: 10 } }, gridcolor: "#e5e7eb" },
                  zaxis: { title: { text: "κᵥ [mm⁻¹]", font: { size: 10 } },
                           gridcolor: "#e5e7eb", range: zRange },
                  aspectmode: "manual", aspectratio: { x: 2, y: 1, z: 0.8 },
                  camera: curvCamRef.current,
                },
                showlegend: false,
              }}
              onRelayout={e => { if (e["scene.camera"]) curvCamRef.current = e["scene.camera"]; }}
              config={commonConfig}
              style={{ width: "100%", height: "100%" }}
              useResizeHandler
            />
          </div>
        </div>

        <div className="w-px bg-gray-200 shrink-0" />

        {/* Right — skeleton surface (x,y,z) */}
        <div className="flex-1 min-w-0 p-3">
          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1 ml-1">
            Skeleton surface — physical space [mm]
          </p>
          <div style={{ height: "calc(100% - 20px)" }}>
            {hasSurface ? (
              <Plot
                data={skelTraces}
                layout={{
                  margin: { l: 0, r: 0, t: 0, b: 0 },
                  paper_bgcolor: "transparent",
                  scene: {
                    xaxis: { title: { text: "x [mm]", font: { size: 10 } }, gridcolor: "#e5e7eb" },
                    yaxis: { title: { text: "y [mm]", font: { size: 10 } }, gridcolor: "#e5e7eb" },
                    zaxis: { title: { text: "z [mm]", font: { size: 10 } }, gridcolor: "#e5e7eb" },
                    aspectmode: "data",
                    camera: skelCamRef.current,
                  },
                  showlegend: false,
                }}
                onRelayout={e => { if (e["scene.camera"]) skelCamRef.current = e["scene.camera"]; }}
                config={commonConfig}
                style={{ width: "100%", height: "100%" }}
                useResizeHandler
              />
            ) : (
              <div className="h-full flex items-center justify-center text-xs text-gray-400">
                Skeleton surface not available.
              </div>
            )}
          </div>
        </div>

      </div>

    </div>
  );
}
